// @ts-nocheck

import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import isPlainObject from 'lodash/isPlainObject';
import { Autocomplete, Box, Chip, Popover, Typography } from '@mui/material';
import PropTypes, { string } from 'prop-types';

import { ReactComponent as DropdownArrow } from '../../assets/images/DropdownArrow.svg';

const SELECT_ALL_OPTIONS = {
  label: 'Select all options',
  value: 'mui_select_all',
};

const handleGetChipLabel = (option) => {
  if (isPlainObject(option)) return option?.label || '';
  if (typeof option === 'string' || typeof option === 'number') return option;
  return '';
};

const handleGetChipKey = (option) => {
  if (isPlainObject(option)) return option?.value || '';
  if (typeof option === 'string' || typeof option === 'number') return option;
  return '';
};

const DefaultChipComponent = ({
  index,
  option,
  getTagProps,
  disabled = false,
  readOnly = false,
}) => {
  const chipProps = getTagProps({ index });
  if (disabled || readOnly) {
    delete chipProps.onDelete; // Remove delete functionality when component is disabled or read-only
  }
  return (
    <Chip
      {...chipProps}
      size="small"
      title={handleGetChipLabel(option)}
      label={handleGetChipLabel(option)}
      color="primary"
      sx={{
        height: 24,
        borderRadius: '12px',
        maxWidth: '100%',
        '& .MuiChip-label': {
          px: 1,
          lineHeight: '24px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        '& .MuiChip-deleteIcon': {
          fontSize: 16,
          marginRight: '4px',
          color: 'primary.main',
        },
      }}
    />
  );
};

DefaultChipComponent.propTypes = {
  index: PropTypes.number.isRequired,
  option: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    }),
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  getTagProps: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
};

const ChipForViewSelectedOptions = ({
  option,
  index,
  getTagChipLabel = null,
  handleChipDelete,
  disabled = false,
  readOnly = false,
  props = {
    color: 'primary',
    size: 'small',
  },
}) => {
  const handleDelete = useCallback(() => {
    handleChipDelete(index);
  }, [handleChipDelete, index]);

  return (
    <Chip
      color={props.color || 'primary'}
      size={props?.size || 'small'}
      label={getTagChipLabel ? getTagChipLabel(option) : option?.label}
      title={getTagChipLabel ? getTagChipLabel(option) : option?.label}
      onDelete={disabled || readOnly ? undefined : handleDelete}
      sx={{
        maxWidth: '100%',
        '& .MuiChip-label': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      }}
    />
  );
};

ChipForViewSelectedOptions.propTypes = {
  option: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  getTagChipLabel: PropTypes.func,
  handleChipDelete: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  color: PropTypes.oneOf([string]),
  size: PropTypes.oneOf(['small', 'medium']),
  props: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.string,
  ]),
};

const Multiselect = ({
  options,
  limitTags = 1,
  CustomChipComponent = DefaultChipComponent,
  noOptionsMessage = 'No options present.',
  disableSelectAll = false,
  disabled = false,
  readOnly = false,
  getTagChipLabel = handleGetChipLabel,
  ...props
}) => {
  const [chipsPopoverOpen, setChipsPopoverOpen] = useState(false);
  const [componentWidth, setComponentWidth] = useState(0);

  const autocompleteRef = useRef(null); // Reference to the autocomplete component for popover positioning

  /**
   * Effect to track component width changes for responsive popover
   * Updates width on mount and window resize events
   */
  useLayoutEffect(() => {
    const updateWidth = () => {
      if (autocompleteRef.current) {
        setComponentWidth(autocompleteRef?.current?.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  /**
   * Toggles the popover showing additional selected chips
   * Only works when component is not disabled
   * @param {Event} e - Click event
   */
  const toggleChipsPopover = useCallback(
    (e) => {
      if (disabled) {
        return;
      }
      setChipsPopoverOpen(true);
      e.stopPropagation();
    },
    [disabled, setChipsPopoverOpen]
  );

  /**
   * Closes the additional chips popover
   */
  const handleCloseChipsPopover = useCallback(() => {
    setChipsPopoverOpen(false);
  }, [setChipsPopoverOpen]);

  /**
   * @function renderTags
   * @description Renders multiselect tags using Material-UI Chip components.
   * @param {Array} value - Array of tag options.
   * @param {Function} getTagProps - Function to get tag props.
   * @returns {JSX.Element}
   */

  const renderTags = useCallback(
    (value, getTagProps) => {
      const numTags = value?.length;

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            maxWidth: 'calc(100% - 20px)',
          }}
        >
          {/* Render only the limited number of chips */}
          <Box
            sx={{
              flexWrap: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {value?.slice(0, limitTags)?.map((option, index) => (
              <CustomChipComponent
                key={handleGetChipKey(option)}
                index={index}
                option={option}
                disabled={disabled}
                readOnly={readOnly}
                getTagProps={getTagProps}
              />
            ))}
          </Box>
          {numTags > limitTags ? (
            <>
              {/* Show "+X more" indicator when there are additional selections */}
              <Box
                sx={{
                  flexShrink: 0,
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}
              >
                <Chip
                  onClick={toggleChipsPopover}
                  label={`+${numTags - limitTags}`}
                  size={props?.size || 'small'}
                  variant="outlined"
                  sx={{
                    cursor: disabled ? 'default' : 'pointer',
                    border: (theme) => `1px solid ${theme?.palette?.divider}`,
                    '&:hover': {
                      backgroundColor: (theme) => theme?.palette?.action?.hover,
                    },
                  }}
                />
              </Box>
            </>
          ) : null}
        </Box>
      );
    },
    [limitTags, disabled, readOnly, CustomChipComponent]
  );

  // When we want to disable the select all option the disableSelectAll prop is used
  const optionsData = useMemo(() => {
    if (disableSelectAll) {
      return options?.length ? [...options] : [];
    }
    return options?.length ? [SELECT_ALL_OPTIONS, ...options] : [];
  }, [disableSelectAll, options]);

  /**
   * Handles selection changes including select all functionality
   * @param {Event} _e - Change event (unused)
   * @param {Array} value - New selected values
   */
  const handleChange = useCallback(
    (_e, value) => {
      if (value.includes(SELECT_ALL_OPTIONS)) {
        return props?.onChange(options);
      }
      return props?.onChange(value);
    },
    [props?.onChange, options]
  );

  /**
   * Handles individual chip deletion from popover
   * Removes the selected option at the specified index
   * @param {number} indexToDelete - Index of the chip to delete
   */
  const handleChipDelete = useCallback(
    (indexToDelete) => {
      if (!props?.value) return;
      const newValue = props?.value?.filter(
        (_val, index) => index !== indexToDelete
      );
      props.onChange(newValue);
    },
    [props?.value, props?.onChange]
  );

  return (
    <>
      {/* Main Autocomplete Component */}
      <Autocomplete
        popupIcon={<DropdownArrow />}
        {...props}
        getOptionKey={handleGetChipKey}
        fullWidth
        options={optionsData}
        ref={autocompleteRef}
        disabled={disabled}
        readOnly={readOnly}
        limitTags={limitTags}
        onChange={handleChange}
        noOptionsText={
          <Typography variant="body2">{noOptionsMessage}</Typography>
        }
        multiple
        renderValue={renderTags}
        sx={{
          '&.MuiAutocomplete-root .MuiAutocomplete-inputRoot': {
            flexWrap: 'nowrap',
            overflow: 'hidden',
          },
        }}
      />
      {/* Popover for Additional Selected Chips */}
      <Popover
        open={
          chipsPopoverOpen && props?.value && props?.value?.length > limitTags
        }
        anchorEl={autocompleteRef.current}
        onClose={handleCloseChipsPopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              p: 1,
              mt: 0.5,
              maxHeight: '190px',
              overflowY: 'auto',
              width: componentWidth,
              boxSizing: 'border-box',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              boxShadow: theme.shadows[4],
              border: `1px solid ${theme?.palette?.divider}`,
            }),
          },
        }}
      >
        {/* Render chips for selections beyond the limit */}
        {(props?.value ?? []).map((option, index) => {
          if (index < limitTags) {
            return null; // Skip chips that are already shown in the main inputs
          }
          return (
            <ChipForViewSelectedOptions
              option={option}
              index={index}
              key={`popover-chip-${option?.value || option?.label || index}`}
              getTagChipLabel={getTagChipLabel}
              props={props}
              handleChipDelete={handleChipDelete}
              disabled={disabled}
              readOnly={readOnly}
            />
          );
        })}
      </Popover>
    </>
  );
};
Multiselect.propTypes = {
  options: PropTypes.oneOf([PropTypes.object]).isRequired,
  limitTags: PropTypes.number,
  CustomChipComponent: PropTypes.elementType,
  noOptionsMessage: PropTypes.string,
  disableSelectAll: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  getTagChipLabel: PropTypes.func,
  value: PropTypes.arrayOf([PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['small', 'medium']),
};

export default Multiselect;
